import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import type { ProjectTrack } from '../api/types';
import { formatDate } from '../lib/format';

const STATUS_COLOR: Record<string, 'error' | 'warning' | 'primary' | 'success' | 'inherit'> = {
  late: 'error',
  at_risk: 'warning',
  in_progress: 'primary',
  complete: 'success',
  not_started: 'inherit',
};

interface Props {
  track: ProjectTrack;
}

export function TrackProgressBar({ track }: Props) {
  const items = track.items ?? [];
  const hasItems = items.length > 0;
  // progressPct is the backend roll-up (weighted by sub-task size); fall back to
  // the track's own counter for older payloads that don't carry it.
  const pct =
    track.progressPct ??
    (track.totalUnits > 0 ? Math.round((track.currentUnits / track.totalUnits) * 100) : 0);
  // computedStatus carries the derived Late/AtRisk urgency the stored status never has.
  const displayStatus = track.computedStatus ?? track.status;
  const color = STATUS_COLOR[displayStatus] ?? 'primary';
  const due = track.dueDate ? ` · due ${formatDate(track.dueDate)}` : '';
  // With sub-tasks the % comes from them, so the track's own unit counter is no
  // longer the headline — show the rolled-up % and the sub-task count instead.
  const caption = hasItems
    ? `${pct}% · ${items.length} sub-task${items.length === 1 ? '' : 's'}${due}`
    : `${track.currentUnits} / ${track.totalUnits} ${track.unitLabel}${due}`;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="body2" fontWeight={500}>
          {track.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
      <Tooltip title={`${pct}% — ${displayStatus.replace(/_/g, ' ')}`}>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Tooltip>
    </Box>
  );
}
